/**
 * CitationModal — Full Document Viewer with Highlighted References
 *
 * 1 document = 1 source. When clicked, fetches the full document from
 * the API and renders it as scrollable markdown. Sections that the AI
 * cited are highlighted with a colored left border so the user can
 * quickly find what was referenced.
 *
 * SourcesPanel deduplicates by document — shows unique documents only.
 */

import { useState, useEffect, useRef } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  FileText, Copy, CheckCheck, ExternalLink, Loader2, AlertTriangle,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiClient } from '../../lib/api-client';

interface Citation {
  docName: string;
  page: number | null;
  section: string | null;
  text: string;
  score?: number;
  classification?: string;
  documentId?: string;
}

interface DocumentChunk {
  chunkIndex: number;
  text: string;
  pageNumber: number | null;
  section: string | null;
}

interface FullDocumentResponse {
  documentId: string;
  documentName: string;
  fullText: string;
  chunks: DocumentChunk[];
}

interface CitationModalProps {
  /** All citations for this document (may be multiple chunks from same doc) */
  citations: Citation[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CitationModal({ citations, open, onOpenChange }: CitationModalProps) {
  const [fullDoc, setFullDoc] = useState<FullDocumentResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const highlightRefs = useRef<(HTMLDivElement | null)[]>([]);

  const primaryCitation = citations[0];
  const documentId = primaryCitation?.documentId;
  const docName = primaryCitation?.docName || 'Untitled Source';
  const citedTexts = citations.map(c => c.text).filter(t => t && t.trim().length > 0);

  // Fetch full document when opened
  useEffect(() => {
    highlightRefs.current = [];
    if (!open || !documentId) {
      setFullDoc(null);
      setError(null);
      return;
    }

    let cancelled = false;
    // #17 — actually abort the in-flight request on unmount/param-change, not
    // just guard the state updates. Otherwise the full-text fetch ran to
    // completion on the server after the modal closed.
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    apiClient.get<FullDocumentResponse>(`/knowledge/documents/${documentId}/full-text`, {
      signal: controller.signal,
    })
      .then(data => {
        if (!cancelled) setFullDoc(data);
      })
      .catch((err) => {
        // Ignore the abort we triggered ourselves; surface real failures.
        if (!cancelled && (err as { name?: string })?.name !== 'AbortError') {
          setError('Could not load full document');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [open, documentId]);

  // Scroll to first highlighted chunk after loading
  useEffect(() => {
    if (fullDoc && highlightRefs.current[0]) {
      setTimeout(() => {
        highlightRefs.current[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [fullDoc]);

  // Early return AFTER all hooks to satisfy Rules of Hooks
  if (!primaryCitation) return null;

  const handleCopy = async () => {
    const text = fullDoc?.fullText ?? citedTexts.join('\n\n');
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  /**
   * Check if a document chunk matches one of the cited passages.
   * Citation text is up to 1000 chars (may be truncated from the full chunk).
   * Match by checking if the chunk starts with the same text as a citation.
   * Uses first 100 chars to avoid false positives from common prefixes.
   */
  function isChunkCited(chunkText: string): boolean {
    if (citedTexts.length === 0 || !chunkText) return false;
    // Normalize: collapse whitespace for comparison
    const normalize = (s: string) => s.replace(/\s+/g, ' ').trim();
    const chunkNorm = normalize(chunkText);
    return citedTexts.some(cited => {
      const citedNorm = normalize(cited);
      // Match if first 100 chars are identical (chunk text starts with citation text or vice versa)
      const compareLen = Math.min(100, citedNorm.length, chunkNorm.length);
      if (compareLen < 20) return false; // Too short to be a reliable match
      return chunkNorm.substring(0, compareLen) === citedNorm.substring(0, compareLen);
    });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[560px] sm:w-[640px] sm:max-w-[640px] p-0 flex flex-col gap-0">

        {/* ── Header ───────────────────────────────────────────────── */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border flex-shrink-0">
          <SheetTitle className="flex items-center gap-3 text-base">
            <FileText className="h-5 w-5 text-primary flex-shrink-0" />
            <span className="truncate">{docName}</span>
          </SheetTitle>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className="text-xs">
              {citations.length} reference{citations.length !== 1 ? 's' : ''} cited
            </Badge>
            {primaryCitation.classification && primaryCitation.classification !== 'Unclassified' && (
              <Badge variant="destructive" className="text-xs">{primaryCitation.classification}</Badge>
            )}
          </div>
        </SheetHeader>

        {/* ── Document Content ──────────────────────────────────────── */}
        {/* bug #7 — the shared ScrollArea's Radix viewport uses display:table,
            which sizes to CONTENT width; wide prose/tables then overflowed and
            the root's overflow-hidden CLIPPED them on the right (no h-scrollbar).
            A plain block scroll container lets prose wrap normally. */}
        <div className="flex-1 overflow-y-auto chat-scroll min-w-0">
          <div className="px-6 py-5 min-w-0 max-w-full break-words">
            {loading && (
              <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Loading document...</span>
              </div>
            )}

            {error && !loading && (
              <div>
                {/* Fallback: show the cited chunks we have */}
                <div className="rounded-xl border border-dashed border-border px-4 py-3 mb-4 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-muted-foreground">{error}. Showing cited passages only.</p>
                </div>
                {citedTexts.map((text, i) => (
                  <div key={i} className="border-l-2 border-primary bg-primary/5 dark:bg-primary/10 rounded-r-xl px-4 py-3 mb-3">
                    <ChunkMarkdown text={text} />
                  </div>
                ))}
              </div>
            )}

            {fullDoc && !loading && (
              <div className="space-y-0">
                {fullDoc.chunks.map((chunk, i) => {
                  const cited = isChunkCited(chunk.text);
                  let highlightIdx = -1;
                  if (cited) {
                    highlightIdx = highlightRefs.current.length;
                  }

                  return (
                    <div
                      key={i}
                      ref={cited ? (el) => { highlightRefs.current.push(el); } : undefined}
                      className={cn(
                        'py-1',
                        cited && 'border-l-3 border-primary bg-primary/5 dark:bg-primary/8 pl-4 pr-2 py-3 my-2 rounded-r-xl',
                      )}
                    >
                      {cited && chunk.section && (
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-primary/70 mb-1">
                          Referenced section{chunk.pageNumber ? ` · Page ${chunk.pageNumber}` : ''}
                        </p>
                      )}
                      <ChunkMarkdown text={chunk.text} />
                    </div>
                  );
                })}
              </div>
            )}

            {/* No documentId — show citation text only */}
            {!documentId && !loading && (
              <div>
                {citedTexts.map((text, i) => (
                  <div key={i} className="mb-3">
                    <ChunkMarkdown text={text} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Footer ───────────────────────────────────────────────── */}
        <div className="flex items-center gap-2 px-6 py-3 border-t border-border flex-shrink-0">
          <Button variant="outline" size="sm" onClick={handleCopy} className="gap-1.5">
            {copied ? <CheckCheck className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied' : 'Copy text'}
          </Button>
          {documentId && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(`/library/doc/${documentId}`, '_blank')}
              className="gap-1.5"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Open in Library
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Markdown renderer for individual chunks inside the document viewer.
 */
function ChunkMarkdown({ text }: { text: string }) {
  return (
    <div className="text-sm leading-[1.7] text-foreground">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p({ children }) { return <p className="my-1 text-sm leading-[1.7] text-foreground">{children}</p>; },
          h1({ children }) { return <h1 className="text-xl font-bold mt-5 mb-2 text-foreground">{children}</h1>; },
          h2({ children }) { return <h2 className="text-lg font-bold mt-4 mb-1.5 text-foreground">{children}</h2>; },
          h3({ children }) { return <h3 className="text-base font-semibold mt-3 mb-1 text-foreground">{children}</h3>; },
          h4({ children }) { return <h4 className="text-sm font-semibold mt-2 mb-1 text-foreground">{children}</h4>; },
          ul({ children }) { return <ul className="list-disc pl-5 space-y-0.5 my-1 text-sm text-foreground">{children}</ul>; },
          ol({ children }) { return <ol className="list-decimal pl-5 space-y-0.5 my-1 text-sm text-foreground">{children}</ol>; },
          li({ children }) { return <li className="text-sm leading-[1.7] text-foreground">{children}</li>; },
          strong({ children }) { return <strong className="font-semibold text-foreground">{children}</strong>; },
          em({ children }) { return <em className="italic">{children}</em>; },
          code({ children }) { return <code className="bg-muted/80 px-1.5 py-0.5 rounded-md text-[13px] font-mono break-words">{children}</code>; },
          pre({ children }) { return <pre className="my-2 overflow-x-auto rounded-lg bg-muted/60 p-3 text-[13px] whitespace-pre-wrap break-words">{children}</pre>; },
          table({ children }) {
            return <div className="my-2 overflow-x-auto rounded-lg border border-border"><table className="w-full text-sm">{children}</table></div>;
          },
          thead({ children }) { return <thead className="bg-muted/50 border-b border-border">{children}</thead>; },
          tbody({ children }) { return <tbody className="divide-y divide-border/50">{children}</tbody>; },
          th({ children }) { return <th className="px-3 py-1.5 text-left text-xs font-bold text-foreground">{children}</th>; },
          td({ children }) { return <td className="px-3 py-1.5 text-sm text-foreground">{children}</td>; },
          blockquote({ children }) { return <blockquote className="border-l-2 border-border pl-3 text-foreground/80 my-2 italic">{children}</blockquote>; },
          a({ href, children }) { return <a href={href} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">{children}</a>; },
          hr() { return <hr className="my-3 border-border" />; },
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

// ── Sources Panel (below response) ──────────────────────────────────────────

interface SourcesPanelProps {
  citations: Citation[];
  onCitationClick: (index: number) => void;
}

/**
 * Deduplicated sources panel — 1 entry per unique document.
 * Shows reference count per document.
 */
export function SourcesPanel({ citations, onCitationClick }: SourcesPanelProps) {
  if (citations.length === 0) return null;

  // Group by document name (dedup)
  const grouped = new Map<string, { indices: number[]; citation: Citation }>();
  citations.forEach((c, i) => {
    const key = c.documentId ?? c.docName ?? `source-${i}`;
    const existing = grouped.get(key);
    if (existing) {
      existing.indices.push(i);
    } else {
      grouped.set(key, { indices: [i], citation: c });
    }
  });

  const uniqueDocs = Array.from(grouped.values());
  const docCount = uniqueDocs.length;

  return (
    <div className="mt-4 pt-0">
      <details className="group" open>
        <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5 select-none">
          <span className="font-medium">
            {docCount} Source{docCount !== 1 ? 's' : ''}
          </span>
        </summary>
        <div className="mt-2 flex flex-wrap gap-2">
          {uniqueDocs.map(({ indices, citation }) => (
            <button
              key={indices[0]}
              onClick={() => onCitationClick(indices[0]!)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border/60 text-xs hover:bg-muted transition-colors"
            >
              <FileText className="h-3 w-3 text-muted-foreground" />
              <span className="truncate max-w-[200px]">{citation.docName}</span>
              {indices.length > 1 && (
                <span className="text-muted-foreground">({indices.length} refs)</span>
              )}
            </button>
          ))}
        </div>
      </details>
    </div>
  );
}
